import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import ApiService from "../services/ApiService";
import "./BookingCalendar.css";

const BookingCalendar = () => {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [disabledDates, setDisabledDates] = useState([]);
  const [userCredits, setUserCredits] = useState({
    credits: 0,
    payPerUseCredits: 0,
  });
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  useEffect(() => {
    const fetchUserCredits = async () => {
      try {
        const response = await ApiService.getUserProfile(currentUser.uid);
        const user = response.data;
        setUserCredits({
          credits: user.credits || 0,
          payPerUseCredits: user.payPerUseCredits || 0,
        });
      } catch (error) {
        console.error("Error fetching user credits:", error);
      }
    };

    if (currentUser) {
      fetchUserCredits();
    }
  }, [currentUser]);
  // Fetch disabled dates
  useEffect(() => {
    const fetchDisabledDates = async () => {
      try {
        const response = await ApiService.getUpcomingDisabledDates();
        console.log("Fetched disabled dates from API:", response.data);
        setDisabledDates(response.data || []);
      } catch (error) {
        console.error("Error fetching disabled dates:", error);
        // Don't show error for disabled dates as it's not critical
      }
    };

    fetchDisabledDates();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      const fetchAvailableSlots = async () => {
        if (!selectedDate) return;

        try {
          setLoading(true);
          const dateStr = selectedDate.toISOString().split("T")[0];
          const response = await ApiService.getAvailableSlots(dateStr);
          setAvailableSlots(response.data || []);
        } catch (error) {
          console.error("Error fetching available slots:", error);
          setError("Failed to load available time slots");
        } finally {
          setLoading(false);
        }
      };

      fetchAvailableSlots();
    }
  }, [selectedDate]);
  // Helper function to check if a date is disabled
  const isDateDisabled = (date) => {
    const dateStr = date.toISOString().split("T")[0];
    console.log("Checking if date is disabled:", dateStr);
    console.log(
      "Disabled dates:",
      disabledDates.map((d) => ({
        original: d.date,
        formatted:
          typeof d.date === "string"
            ? d.date.split("T")[0]
            : new Date(d.date).toISOString().split("T")[0],
      }))
    );

    return disabledDates.some((disabledDate) => {
      // Handle the date comparison more carefully
      let disabledDateStr;
      if (typeof disabledDate.date === "string") {
        // If it's already a string in YYYY-MM-DD format
        disabledDateStr = disabledDate.date.split("T")[0];
      } else {
        // If it's a Date object
        disabledDateStr = new Date(disabledDate.date)
          .toISOString()
          .split("T")[0];
      }
      const isMatch = disabledDateStr === dateStr;
      console.log(`Comparing ${disabledDateStr} === ${dateStr}: ${isMatch}`);
      return isMatch;
    });
  };

  // Helper function to get disabled date reason
  const getDisabledDateReason = (date) => {
    const dateStr = date.toISOString().split("T")[0];
    const disabledDate = disabledDates.find((disabledDate) => {
      // Handle the date comparison more carefully
      let disabledDateStr;
      if (typeof disabledDate.date === "string") {
        // If it's already a string in YYYY-MM-DD format
        disabledDateStr = disabledDate.date.split("T")[0];
      } else {
        // If it's a Date object
        disabledDateStr = new Date(disabledDate.date)
          .toISOString()
          .split("T")[0];
      }
      return disabledDateStr === dateStr;
    });
    return disabledDate?.reason || "Booking not available";
  };

  const handleDateSelect = (date) => {
    // Check if the date is disabled
    if (isDateDisabled(date)) {
      const reason = getDisabledDateReason(date);
      setError(
        `This date is not available for booking. ${
          reason ? `Reason: ${reason}` : ""
        }`
      );
      return;
    }

    setSelectedDate(date);
    setSelectedSlot(null);
    setError("");
    setSuccess("");
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setError("");
  };

  const handleBooking = async () => {
    if (!selectedDate || !selectedSlot) {
      setError("Please select a date and time slot");
      return;
    } // Check if user has sufficient credits (pay-per-use first, then regular)
    if (userCredits.payPerUseCredits < 1 && userCredits.credits < 1) {
      setError("You don't have enough credits. Please purchase a plan.");
      return;
    }
    try {
      setBooking(true);
      setError("");

      // Determine service type based on available credits (pay-per-use first)
      const actualServiceType =
        userCredits.payPerUseCredits > 0 ? "pay_per_use" : "regular";

      const bookingData = {
        slotId: selectedSlot.id,
        bookingDate: selectedDate.toISOString().split("T")[0],
        serviceType: actualServiceType,
      };

      const response = await ApiService.createBooking(bookingData);
      const bookingResult = response.data || response;

      setSuccess("Booking confirmed successfully!"); // Update user credits locally (use pay-per-use first, then regular)
      if (userCredits.payPerUseCredits > 0) {
        setUserCredits((prev) => ({
          ...prev,
          payPerUseCredits: prev.payPerUseCredits - 1,
        }));
      } else {
        setUserCredits((prev) => ({
          ...prev,
          credits: prev.credits - 1,
        }));
      }

      // Reset selection
      setSelectedDate(null);
      setSelectedSlot(null); // Redirect to booking confirmation page with booking details
      setTimeout(() => {
        navigate("/booking-confirmation", {
          state: {
            bookingDetails: {
              bookingId: bookingResult.id,
              bookingDate: selectedDate.toISOString().split("T")[0],
              slot: selectedSlot,
              serviceType: actualServiceType,
            },
          },
        });
      }, 2000);
    } catch (error) {
      console.error("Booking error:", error);
      setError(error.message || "Failed to create booking");
    } finally {
      setBooking(false);
    }
  };
  const generateCalendarDays = () => {
    const today = new Date();
    const days = [];

    // Generate next 10 days
    for (let i = 0; i < 10; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      days.push(date);
    }

    return days;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeStr) => {
    return timeStr;
  };

  return (
    <div className="booking-calendar-container">
      <div className="booking-header">
        <h2>Book Your Service</h2>
        <div className="credits-display">
          <div className="credit-item">
            <span className="credit-label">Credits:</span>
            <span className="credit-value">{userCredits.credits}</span>
          </div>
          <div className="credit-item">
            <span className="credit-label">Pay-per-use Credits:</span>
            <span className="credit-value">{userCredits.payPerUseCredits}</span>
          </div>
        </div>
      </div>
      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}
      {success && (
        <div className="success-message">
          <p>{success}</p>
        </div>
      )}
      {/* Date Selection */}
      <div className="date-selection">
        <h3>Select Date</h3>{" "}
        <div className="calendar-grid">
          {generateCalendarDays().map((date, index) => {
            const isDisabled = isDateDisabled(date);
            return (
              <div
                key={index}
                className={`calendar-day ${
                  selectedDate?.toDateString() === date.toDateString()
                    ? "selected"
                    : ""
                } ${isDisabled ? "disabled" : ""}`}
                onClick={() => handleDateSelect(date)}
                title={isDisabled ? getDisabledDateReason(date) : ""}
              >
                <div className="day-name">{formatDate(date)}</div>
                <div className="day-number">{date.getDate()}</div>
                {isDisabled && <div className="disabled-indicator">🚫</div>}
              </div>
            );
          })}
        </div>
      </div>
      {/* Time Slot Selection */}
      {selectedDate && (
        <div className="slot-selection">
          <h3>Select Time Slot for {formatDate(selectedDate)}</h3>

          {loading ? (
            <div className="loading-slots">
              <div className="spinner"></div>
              <p>Loading available slots...</p>
            </div>
          ) : (
            <div className="slots-grid">
              {availableSlots.map((slot) => (
                <div
                  key={slot.id}
                  className={`time-slot ${
                    !slot.isAvailable ? "unavailable" : ""
                  } ${selectedSlot?.id === slot.id ? "selected" : ""}`}
                  onClick={() => slot.isAvailable && handleSlotSelect(slot)}
                >
                  <div className="slot-time">
                    {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                  </div>
                  <div className="slot-availability">
                    {slot.isAvailable
                      ? `${slot.availableCapacity} slot${
                          slot.availableCapacity !== 1 ? "s" : ""
                        } available`
                      : "Fully booked"}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && availableSlots.length === 0 && (
            <div className="no-slots">
              <p>No time slots available for the selected date.</p>
              <p>Please choose a different date.</p>
            </div>
          )}
        </div>
      )}
      {/* Booking Summary and Confirmation */}
      {selectedDate && selectedSlot && (
        <div className="booking-summary">
          <h3>Booking Summary</h3>
          <div className="summary-details">
            <div className="summary-row">
              <span>Date:</span>
              <span>{formatDate(selectedDate)}</span>
            </div>
            <div className="summary-row">
              <span>Time:</span>
              <span>
                {formatTime(selectedSlot.startTime)} -{" "}
                {formatTime(selectedSlot.endTime)}
              </span>{" "}
            </div>
            <div className="summary-row">
              <span>Credits Used:</span>
              <span>1 credit</span>
            </div>
          </div>

          <button
            onClick={handleBooking}
            disabled={booking}
            className="confirm-booking-button"
          >
            {booking ? "Booking..." : "Confirm Booking"}
          </button>
        </div>
      )}{" "}
      {/* No Credits Message */}
      {userCredits.credits === 0 && userCredits.payPerUseCredits === 0 && (
        <div className="no-credits-message">
          <h3>No Credits Available</h3>
          <p>You don't have any credits to make a booking.</p>
          <button
            onClick={() => navigate("/integrated-purchase-flow")}
            className="purchase-credits-button"
          >
            Purchase Credits
          </button>
        </div>
      )}
    </div>
  );
};

export default BookingCalendar;
